import {createContext, useContext, type ReactNode} from 'react'
import type {UserState} from '../lib/utils/rendering-strategy'

/**
 * Context for user personalization data.
 * Currently contains user state for rendering strategies, but can be extended
 * to include org membership, subscription tier, or other user attributes.
 */
export type PersonalizationContextValue = {
  userState: UserState
}

const PersonalizationContext = createContext<PersonalizationContextValue | undefined>(undefined)

export type PersonalizationProviderProps = {
  children: ReactNode
  value: PersonalizationContextValue
}

/**
 * Provider for personalization context. Mount at template level to make
 * user state available to all child components for conditional rendering.
 */
export function PersonalizationProvider({children, value}: PersonalizationProviderProps) {
  return <PersonalizationContext.Provider value={value}>{children}</PersonalizationContext.Provider>
}

/**
 * Hook to access personalization context.
 * Returns a default empty state if used outside of a PersonalizationProvider
 * to allow components to be used in isolation (e.g., Storybook, tests).
 */
export function usePersonalization(): PersonalizationContextValue {
  const context = useContext(PersonalizationContext)
  if (!context) {
    // Graceful fallback for components used outside personalization context
    return {userState: {}}
  }
  return context
}

try{ PersonalizationContext.displayName ||= 'PersonalizationContext' } catch {}
try{ PersonalizationProvider.displayName ||= 'PersonalizationProvider' } catch {}